import { placeExportedLabels } from '@components/ArtBoard/Arrows'
import { CAPTURE_CLASS } from '@components/ArtBoard/Canvas/constants'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { Options } from 'html2canvas'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import {
  EXPORT_CANVAS_PADDING,
  MAX_CANVAS_RESOLUTION,
  THEME_BLUEPRINT,
  THEME_DARK,
} from '@/constants/constants'
import { useDomToBase64, useScaleZoom } from '@/hooks'
import { ImageType } from '@/types/enums'
import { getPngCaptureBounds } from './captureBounds'

type PngExportActions = {
  setTransparent: (e: boolean) => void
  setX2resolution: (e: boolean) => void
  setLogoAndTitle: (e: boolean) => void
  setStickyNotes: (e: boolean) => void
  setColorLegend: (e: boolean) => void
}

type PngExportProps = {
  transparent: boolean
  x2resolution: boolean
  logoAndTitle: boolean
  stickyNotes: boolean
  colorLegend: boolean
}

type UsePngExportProps = {
  actions: PngExportActions
  props: PngExportProps

  isSaving: boolean
  overSized: boolean

  height: number
  width: number
  zoom: number

  save: () => void
}

const getScale = (
  props: PngExportProps,
  domRect: ReturnType<typeof getPngCaptureBounds>
): number => {
  const limit = Math.max(domRect.width, domRect.height)

  if (limit * 2 > MAX_CANVAS_RESOLUTION) {
    return MAX_CANVAS_RESOLUTION / limit
  }

  return props.x2resolution ? 2 : 1
}

const getCanvasOptions = (
  props: PngExportProps,
  color: string,
  domRect: ReturnType<typeof getPngCaptureBounds> | null
): Partial<Options> => {
  if (!domRect) {
    return {}
  }

  const scale = getScale(props, domRect)
  const width = domRect.width * scale + EXPORT_CANVAS_PADDING * scale
  // The print stylesheet adds 96px above the tree; keep 70px below it.
  const height = (domRect.height + 96 + 70) * scale
  const backgroundColor = props.transparent ? null : color

  return {
    removeContainer: true,
    allowTaint: false,
    scale: 1,
    width,
    height,
    x: domRect.left * scale,
    y: domRect.top * scale,
    backgroundColor,
    onclone: async (doc, e) => {
      e.classList.add('--print')
      e.classList.add('--png')

      e.querySelectorAll('.block-user').forEach((el) => {
        el.classList.add('--hidden')
      })

      e.querySelectorAll('.Arrow-model-external-link').forEach((el) => {
        el.classList.add('--hidden')
      })

      e.querySelectorAll('.Arrow-model-attachment').forEach((el) => {
        el.classList.add('--hidden')
      })

      if (props.transparent) {
        e.classList.add('--transparent')
      }

      if (props.stickyNotes) {
        e.classList.add('--sticky-notes')
      }

      if (props.logoAndTitle) {
        const projectName = doc.querySelector<HTMLDivElement>('#project-header')

        if (projectName) {
          projectName.style.display = 'none'
        }
      }

      const imageSources = e.querySelectorAll<HTMLImageElement>(
        '.block-image > img, .node-image > img'
      )

      if (imageSources) {
        // @TODO refactor this, find way to change generator way and unexpected await inside loop
        for (const image of Array.from(imageSources)) {
          // biome-ignore lint/suspicious/noAsyncPromiseExecutor: see TODO above
          await new Promise(async (resolve) => {
            const url = image.getAttribute('src')

            const base64: string = await fetch(url as string)
              .then((response) => response.blob())
              .then((blob) => {
                return new Promise((resolve) => {
                  const reader = new FileReader()

                  reader.onload = (result) => {
                    // @ts-expect-error
                    resolve(result.currentTarget?.result)
                  }

                  reader.readAsDataURL(blob)
                })
              })

            image.style.backgroundImage = `url(${base64})`
            image.style.backgroundSize = 'contain'
            image.style.backgroundPositionX = 'center'
            image.style.backgroundPositionY = 'center'
            image.style.backgroundRepeat = 'no-repeat'
            image.style.display = 'block'

            // @TODO review - no-promise-executor-return
            return setTimeout(resolve, 100)
          })
        }
      }

      const legendTree = e.querySelector<HTMLDivElement>('#legend')

      if (legendTree && props.transparent) {
        legendTree.style.backgroundColor = 'transparent'
      }

      if (legendTree && !props.colorLegend) {
        legendTree.style.opacity = '0'
      }

      const ghostNodes = e.querySelectorAll<HTMLDivElement>('.Cell-ghost')

      if (props.transparent) {
        Array.from(ghostNodes).forEach((node) => {
          node.style.background = 'transparent'
        })
      }

      // hack: The html2canvas library could not catch svg absolute position
      // In this case, the position is swapped
      Array.from(
        e.querySelectorAll<HTMLDivElement>(
          '.Tree-arrows > div.Arrow-line > div > div > div'
        )
      ).forEach((e) => {
        const child = e.querySelector('svg')

        if (child) {
          e.style.left = child.style.left
          e.style.top = child.style.top
        }
      })

      Array.from(
        e.querySelectorAll<HTMLDivElement>(
          '.Tree-arrows > div.Arrow-smooth > div > div'
        )
      ).forEach((e) => {
        const child = e.querySelector('svg')

        if (child) {
          e.style.left = child.style.left
          e.style.top = child.style.top
        }
      })

      Array.from(
        e.querySelectorAll<SVGAElement>('.Tree-arrows > div svg')
      ).forEach((e) => {
        e.style.position = 'static'
        e.style.zIndex = '10000'
      })

      Array.from(
        e.querySelectorAll<HTMLElement>('.element::-webkit-scrollbar')
      ).forEach((e) => {
        e.style.width = '0'
        e.style.height = '0'
      })

      Array.from(e.querySelectorAll<HTMLDivElement>('.node-name')).forEach(
        (e) => {
          const textarea = e.querySelector('textarea')
          const div = e.querySelector('div')

          if (textarea) {
            textarea.style.opacity = '0'
          }

          if (div) {
            div.style.opacity = '1'
          }
        }
      )

      placeExportedLabels(e)

      const projectBadge = e.querySelector<HTMLDivElement>('#project-badge')

      if (projectBadge) {
        projectBadge.style.display = 'none'
      }

      e.style.transform = `scale(${scale}, ${scale})`
    },
  }
}

const usePngExportProps = (
  legendEnabled: boolean,
  onSuccess: () => void,
  projectName?: string
): UsePngExportProps => {
  // todo move this selector to the store
  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  let bgColor = '#fff'
  if (theme === THEME_DARK) {
    bgColor = '#445670'
  }

  if (theme === THEME_BLUEPRINT) {
    bgColor = '#f3f7fb'
  }

  const { zoom } = useScaleZoom()

  const node = document.querySelector('#printed-node')
  const domRect = node ? getPngCaptureBounds(node) : null

  const limit = Math.max(domRect?.width || 0, domRect?.height || 0)
  const overSized = limit * 2 > MAX_CANVAS_RESOLUTION

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [transparent, setTransparent] = useState<boolean>(true)
  const [x2resolution, setX2resolution] = useState<boolean>(!overSized)
  const [logoAndTitle, setLogoAndTitle] = useState<boolean>(true)
  // Temporary removed sticky notes from export until cutting is fixed
  const [stickyNotes, setStickyNotes] = useState<boolean>(false)
  const [colorLegend, setColorLegend] = useState<boolean>(legendEnabled)

  const props = {
    transparent,
    x2resolution,
    logoAndTitle,
    stickyNotes,
    colorLegend,
  }

  const saveAsFile = (base64: string) => {
    const link = document.createElement('a')

    document.body.appendChild(link) // Firefox fix
    link.setAttribute('href', base64)
    link.setAttribute('download', `${projectName}.png`)
    link.click()
    document.body.removeChild(link)
  }

  const options = getCanvasOptions(props, bgColor, domRect)

  const { getStringContext, isConverting } = useDomToBase64(
    document.querySelector('#printed-node'),
    ImageType.PNG
  )

  // The whole map has to be painted before html2canvas clones it: off-screen culling and strips
  // mode (OCT-5072) hide cards with visibility: hidden, and html2canvas copies the computed
  // styles of every SVG node into inline styles while it clones — before onclone runs. A
  // wireframe hidden at that moment therefore stays hidden in the PNG whatever onclone adds to
  // the clone, which is how cards ended up as bare coloured boxes (OCT-5237). The marker is
  // read by Node.module.scss and Block.module.scss and dropped again once the capture is done.
  const startCapture = () => {
    node?.classList.add(CAPTURE_CLASS)
  }

  const endCapture = () => {
    node?.classList.remove(CAPTURE_CLASS)
  }

  const save = () => {
    if (!isSaving) {
      setIsSaving(true)
      startCapture()

      setTimeout(() => {
        // Zoom reset and Compact reflow finish before measuring the capture.
        const captureBounds = node ? getPngCaptureBounds(node) : null
        getStringContext(getCanvasOptions(props, bgColor, captureBounds))
          .then((base64) => {
            setIsSaving(false)
            if (base64) {
              saveAsFile(base64)
              onSuccess()
            }
          })
          .catch((e) => console.error(e))
          .finally(endCapture)
      }, 200)
    }
  }

  return {
    actions: {
      setColorLegend,
      setLogoAndTitle,
      setStickyNotes,
      setTransparent,
      setX2resolution: (e) => {
        if (!overSized) {
          setX2resolution(e)
        }
      },
    },
    props,
    save,
    isSaving: isSaving || isConverting,
    width: Math.floor(options.width || 0),
    height: Math.floor(options.height || 0),
    overSized,
    zoom,
  }
}

export default usePngExportProps
